import type { GroundMode } from '../../types/ground';
import { THEMES as GROUND_THEMES } from '../ground-trip/groundTripUtils';

export type TripTileTheme = {
  stripe: string;
  selectedBorder: string;
  softBorder: string;
  routeDotFrom: string;
  routeDotTo: string;
  routeLine: string;
  routeIcon: string;
  price: string;
  /** Center route distance; defaults to price color when omitted. */
  distance?: string;
  badge: string;
  hoverBorder: string;
  selectedShadow: string;
};

const trainBase = GROUND_THEMES.train;

export const TRIP_TILE_THEMES = {
  train: {
    stripe: 'bg-gradient-to-b from-indigo-500 to-slate-600',
    selectedBorder: trainBase.selectedBorder,
    softBorder: trainBase.softBorder,
    routeDotFrom: trainBase.routeDotFrom,
    routeDotTo: trainBase.routeDotTo,
    routeLine: trainBase.routeLine,
    routeIcon: 'text-indigo-500',
    price: trainBase.price,
    badge: trainBase.badge,
    hoverBorder: 'hover:border-indigo-300/60 dark:hover:border-indigo-500/40',
    selectedShadow: 'shadow-lg shadow-indigo-500/10',
  },
  bus: {
    stripe: 'bg-gradient-to-b from-violet-500 to-fuchsia-500',
    selectedBorder: GROUND_THEMES.bus.selectedBorder,
    softBorder: GROUND_THEMES.bus.softBorder,
    routeDotFrom: GROUND_THEMES.bus.routeDotFrom,
    routeDotTo: GROUND_THEMES.bus.routeDotTo,
    routeLine: GROUND_THEMES.bus.routeLine,
    routeIcon: 'text-violet-500',
    price: GROUND_THEMES.bus.price,
    badge: GROUND_THEMES.bus.badge,
    hoverBorder: 'hover:border-violet-300/60 dark:hover:border-violet-500/40',
    selectedShadow: 'shadow-lg shadow-violet-500/10',
  },
  ferry: {
    stripe: 'bg-gradient-to-b from-cyan-500 to-teal-600',
    selectedBorder: GROUND_THEMES.ferry.selectedBorder,
    softBorder: GROUND_THEMES.ferry.softBorder,
    routeDotFrom: GROUND_THEMES.ferry.routeDotFrom,
    routeDotTo: GROUND_THEMES.ferry.routeDotTo,
    routeLine: GROUND_THEMES.ferry.routeLine,
    routeIcon: 'text-cyan-500',
    price: GROUND_THEMES.ferry.price,
    badge: GROUND_THEMES.ferry.badge,
    hoverBorder: 'hover:border-cyan-300/60 dark:hover:border-cyan-500/40',
    selectedShadow: 'shadow-lg shadow-cyan-500/10',
  },
  flight: {
    stripe: 'bg-gradient-to-b from-sky-500 to-blue-700',
    selectedBorder: 'border-sky-400 dark:border-sky-500',
    softBorder: 'border-stone-200 dark:border-slate-600',
    routeDotFrom: 'bg-sky-500',
    routeDotTo: 'bg-blue-600',
    routeLine: 'border-sky-400/50',
    routeIcon: 'text-sky-500',
    price: 'text-sky-600 dark:text-sky-400',
    distance: 'text-stone-900 dark:text-white',
    badge: 'bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200',
    hoverBorder: 'hover:border-sky-300/60 dark:hover:border-sky-500/40',
    selectedShadow: 'shadow-lg shadow-sky-500/10',
  },
  car: {
    stripe: 'bg-gradient-to-b from-amber-500 to-orange-600',
    selectedBorder: 'border-amber-400 dark:border-amber-500',
    softBorder: 'border-stone-200 dark:border-slate-600',
    routeDotFrom: 'bg-amber-500',
    routeDotTo: 'bg-orange-600',
    routeLine: 'border-amber-400/50',
    routeIcon: 'text-amber-600',
    price: 'text-amber-700 dark:text-amber-300',
    badge: 'bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-100',
    hoverBorder: 'hover:border-amber-300/60 dark:hover:border-amber-500/40',
    selectedShadow: 'shadow-lg shadow-amber-500/10',
  },
} satisfies Record<'train' | 'bus' | 'ferry' | 'flight' | 'car', TripTileTheme>;

export function groundModeTheme(mode: GroundMode): TripTileTheme {
  return TRIP_TILE_THEMES[mode];
}
